import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  ActivateDeviceFormSchema,
  FORM_CONFIG,
  type ActivateDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderSelectControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { AppLogger } from '@/lib/logger'
import { useSecurityContext } from '@/contexts/SecurityContext'

const log = AppLogger.getLogger(import.meta)

export { ActivateDeviceFormSchema, type ActivateDeviceFormData } from './schema'

export type ActivateDeviceComposerProps = {
  deviceNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
}

const validateField = (name: keyof ActivateDeviceFormData, value: unknown) => {
  const shape = ActivateDeviceFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  return parsed.success ? null : parsed.error.issues[0]?.message
}

export function ActivateDeviceComposer(props: ActivateDeviceComposerProps) {
  const { deviceNumber, onSuccess, onError, onCancel, showHeader = true } = props
  const { userInfo } = useSecurityContext()

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const getDevice = useApiMutation<{ device: any }>('getDeviceByNumber', 'GET', {
    onError: (error) => onError?.(error),
  })
  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => onError?.(error),
  })
  const upsertProvisioning = useApiMutation('upsertDeviceProvisioningInfo', 'POST', {
    onError: (error) => onError?.(error),
  })

  const form = useAppForm<ActivateDeviceFormData>({
    schema: ActivateDeviceFormSchema,
    defaultValues: {
      networkCorridor: '',
      networkSite: '',
    },
    onSubmit: async ({ value }) => {
      // Validate form values
      const corridorNumber = parseInt((value as any).networkCorridor);
      const siteNumber = parseInt((value as any).networkSite);

      log.debug('[activateDevice] Starting workflow:', {
        deviceNumber,
        networkCorridor: (value as any).networkCorridor,
        networkSite: (value as any).networkSite,
        corridorNumber,
        siteNumber,
      });

      // Validate parsed numbers
      if (isNaN(corridorNumber) || corridorNumber === 0) {
        const error = new Error('Invalid network corridor selection');
        log.error('[activateDevice] Invalid corridor number:', (value as any).networkCorridor);
        onError?.(error);
        throw error;
      }

      if (isNaN(siteNumber) || siteNumber === 0) {
        const error = new Error('Invalid network site selection');
        log.error('[activateDevice] Invalid site number:', (value as any).networkSite);
        onError?.(error);
        throw error;
      }

      try {
        // Step 1: Fetch current device data to preserve existing fields
        log.debug('[activateDevice] Fetching existing device data...');
        const currentDevice = await getDevice.mutateAsync({ deviceNumber });
        const existingDevice: any = (currentDevice as any)?.device || {};

        log.debug('[activateDevice] Existing device data:', existingDevice);

        const currentUserNumber =
          userInfo?.userNumber && userInfo.userNumber >= 1000000000 ? userInfo.userNumber : undefined

        // Step 2: Create new provisioning record for live deployment
        const provisioningResponse = await upsertProvisioning.mutateAsync({
          deviceNumber,
          provisioningInfo: {
            deviceNumber,
            provisionedDeviceNumber: deviceNumber,
            ...(currentUserNumber ? { deviceProvisionedRequestedBy: currentUserNumber } : {}),
            networkCorridorNumber: corridorNumber,
            networkSiteNumber: siteNumber,
            deviceProvisioningStatus: 'requested',
            deviceProvisionRequestedDate: Math.floor(Date.now() / 1000),
            deviceProvisioningType: 'live',
          },
        })

        log.debug('[activateDevice] Provisioning response:', provisioningResponse);

        const provisioningInfoNumber =
          provisioningResponse?.content?.provisioningInfo?.deviceProvisioningInfoNumber

        log.debug('[activateDevice] Provisioning info number:', provisioningInfoNumber);

        // Validate provisioning response
        if (!provisioningInfoNumber) {
          log.warn('[activateDevice] Provisioning succeeded but no provisioningInfoNumber returned');
        }

        // Step 3: Update device status to 'live' (MERGE with existing device data)
        const deviceResponse = await upsertDevice.mutateAsync({
          device: {
            ...existingDevice,
            deviceNumber,
            deviceStatus: 'live',
            deviceProvisioningInfoNumber: provisioningInfoNumber,
          },
        })

        log.debug('[activateDevice] Device update response:', deviceResponse);
        log.debug('[activateDevice] Workflow completed successfully:', {
          deviceNumber,
          finalStatus: 'live',
          provisioningInfoNumber,
          corridorNumber,
          siteNumber,
        });

        onSuccess?.()
      } catch (error) {
        log.error('[activateDevice] Failed to activate device:', error);
        onError?.(error as Error)
        throw error
      }
    },
  })

  const [selectedCorridor, setSelectedCorridor] = React.useState<string>('')

  const { optionsMap, loadingMap } = useSelectOptionsMap({
    networkCorridor: {
      endpoint: 'getNetworkCorridors',
      dataKey: 'networkCorridors',
      valueField: 'networkCorridorNumber',
      labelField: 'networkCorridorName',
    },
    networkSite: {
      endpoint: 'getNetworkSites',
      dataKey: 'networkSites',
      valueField: 'networkSiteNumber',
      labelField: 'networkSiteName',
      queryOptions: {
        enabled: !!selectedCorridor,
      },
    },
  })

  const handleCorridorChange = (value: string) => {
    const nextCorridor = String(value || '')
    if (selectedCorridor !== nextCorridor) {
      setSelectedCorridor(nextCorridor)
      form.setFieldValue('networkSite', '')
    }
  }

  const renderControl = (control: ControlConfig) => {
    if (control.htmlControl !== 'select') return null

    let options: Array<{ value: string; label: string }> = []
    let isLoading = false

    if (control.name === 'networkCorridor') {
      options = optionsMap.networkCorridor || []
      isLoading = loadingMap.networkCorridor || false
    } else if (control.name === 'networkSite') {
      options = optionsMap.networkSite || []
      isLoading = loadingMap.networkSite || false
    } else {
      options = FormConfigLoader.normalizeSelectOptions(Array.isArray(control.selectOptions) ? control.selectOptions : [])
    }

    let disabled = control.disabled ?? false
    if (control.name === 'networkSite') {
      disabled = disabled || !selectedCorridor || options.length === 0
    }

    const fieldName = control.name as keyof ActivateDeviceFormData

    return (
      <form.AppField
        key={control.name}
        name={control.name as 'networkCorridor' | 'networkSite'}
        validators={{
          onChange: ({ value }) => {
            const stringValue = value !== undefined && value !== null ? String(value) : ''
            if (control.name === 'networkCorridor') {
              handleCorridorChange(stringValue)
            }
            return undefined
          },
          onBlur: ({ value }) => {
            const stringValue = value !== undefined && value !== null ? String(value) : ''
            return validateField(fieldName, stringValue) || undefined
          },
        }}
      >
        {(bound) =>
          renderSelectControl(
            {
              ...control,
              options,
              isLoading,
              disabled,
            },
            bound,
          )
        }
      </form.AppField>
    )
  }

  const renderFormContent = () =>
    groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map((control) => (
            <div key={control.name}>{renderControl(control)}</div>
          ))}
        </div>
      )
    })

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            {FORM_CONFIG.formDescription && (
              <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
            )}
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-end gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}
          {renderButtonControl(
            {
              label: form.state.isSubmitting
                ? FORM_CONFIG.submitButton?.loadingLabel ?? 'Loading...'
                : FORM_CONFIG.submitButton?.label ?? '',
              variant: 'default',
              type: 'submit',
            },
            { SubmitButton: form.SubmitButton },
          )}
        </div>
      </div>
    </form.AppForm>
  )
}
